import type { ThirdLevelEditorCategory } from '../../types/thirdLevelBid';
import { THIRD_LEVEL_EDITOR_CATEGORIES, THIRD_LEVEL_EDITOR_CATEGORY_LABELS } from '../../types/thirdLevelBid';

interface ThirdLevelEditorToolbarProps {
  /** The selected data category (FR-028); default `Unanswered` is owned by the caller. */
  category: ThirdLevelEditorCategory;
  /** Called when the editor picks a different category from the dropdown. */
  onCategoryChange: (category: ThirdLevelEditorCategory) => void;
  /** Called when the editor clicks "Refresh" — the caller reloads from the database. */
  onRefresh: () => void;
  /** The current hand's per-editor answered status (drives the "Answered/Unanswered" badge). */
  isAnswered: boolean;
  /** The current hand's global assigned status (drives the "Assigned/Unassigned" badge). */
  isAssigned: boolean;
  /** True while a load/assign is in flight — disables the controls. */
  disabled?: boolean;
}

// Coloured status pill — mirrors the Overcalling — Data editing `StateLabel` badge (green for
// the "done" state, red for the "to-do" state). px-2 py-0.5 rounded, same as that page.
function StatusBadge({ active, activeLabel, inactiveLabel }: {
  active: boolean; activeLabel: string; inactiveLabel: string;
}) {
  const classes = active
    ? 'text-green-700 bg-green-100'
    : 'text-red-700 bg-red-100';
  return (
    <span className={`inline-block px-2 py-0.5 text-sm font-semibold rounded ${classes}`}>
      {active ? activeLabel : inactiveLabel}
    </span>
  );
}

// Presentation-only editor toolbar for "Decision to make a 3rd level bid" editor mode (FR-028,
// Session 2026-06-15). Reuses the exact "Data category: [select] [Refresh]" row layout of the
// Overcalling — Data editing page (placed directly under the page header), plus the per-hand
// status badges as coloured pills. It holds NO state and issues NO API call — selection state
// and all actions are injected via props (constitution II/III). Editor mode is exempt from the
// accessibility requirements (FR-027), but basic label/aria hygiene is kept anyway.
export function ThirdLevelEditorToolbar({
  category,
  onCategoryChange,
  onRefresh,
  isAnswered,
  isAssigned,
  disabled = false,
}: ThirdLevelEditorToolbarProps) {
  return (
    <div
      className="flex items-center flex-wrap gap-2 mb-4 short:mb-2"
      data-testid="third-level-editor-toolbar"
    >
      <label htmlFor="third-level-data-category" className="text-sm font-bold text-ink">
        Data category:
      </label>
      <select
        id="third-level-data-category"
        value={category}
        disabled={disabled}
        aria-label="Data category"
        data-testid="third-level-editor-category"
        onChange={e => onCategoryChange(e.target.value as ThirdLevelEditorCategory)}
        className="border border-line-strong rounded-md px-2 py-1 text-sm"
      >
        {THIRD_LEVEL_EDITOR_CATEGORIES.map(c => (
          <option key={c} value={c}>{THIRD_LEVEL_EDITOR_CATEGORY_LABELS[c]}</option>
        ))}
      </select>
      <button
        type="button"
        onClick={onRefresh}
        disabled={disabled}
        aria-label="Refresh"
        data-testid="third-level-editor-refresh"
        className="text-sm font-medium text-ink bg-cream hover:bg-paper disabled:opacity-50 disabled:cursor-not-allowed border border-line-strong rounded-md px-3 py-1"
      >
        Refresh
      </button>

      {/* Current-hand status as coloured pills (FR-028). */}
      <span className="w-px h-5 bg-line-strong mx-1" aria-hidden="true" />
      <span data-testid="third-level-editor-answered-status">
        <StatusBadge active={isAnswered} activeLabel="Answered" inactiveLabel="Unanswered" />
      </span>
      <span data-testid="third-level-editor-assigned-status">
        <StatusBadge active={isAssigned} activeLabel="Assigned" inactiveLabel="Unassigned" />
      </span>
    </div>
  );
}
